import {
  DEFAULT_ANIMATE_DURATION,
  NextImage,
  noForwardProps
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ApostleSkillItem from '@src/components/apostles/share/ApostleSkillItem'
import ParagraphBlockLinkButton from '@src/components/battle/demo/blocks/ParagraphBlockLinkButton'
import {
  ApostleSkillsProvider,
  useApostleSkillsContext
} from '@src/contexts/apostle/ApostleSkillsContext'
import { PvpBuildExampleBlock } from '@src/types/pageBuilder'
import { darken } from 'polished'
import * as R from 'ramda'

const Container = styled(Box)`
  position: relative;
  width: 100%;
`

const PreviewContainer = styled(Box)`
  border-radius: 8px;
  overflow: hidden;
  transition: transform ${DEFAULT_ANIMATE_DURATION}ms,
    filter ${DEFAULT_ANIMATE_DURATION}ms;
  :hover {
    filter: brightness(1.2) saturate(120%);
    transform: translateY(-3px);
  }
  width: 100%;
`

const Preview = styled(NextImage, noForwardProps)<{ __aspectRatio: string }>`
  aspect-ratio: ${({ __aspectRatio }) => __aspectRatio};
  width: 100%;
`

const Apostles = styled(Box)`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  margin-top: 12px;
`

const ApostleContainer = styled(Box)`
  align-items: center;
  background: #12151c;
  border-radius: 4px;
  border: 2px solid rgba(128, 128, 128, 0.25);
  display: flex;
  flex-direction: row;
  justify-content: center;
  transition: transform ${DEFAULT_ANIMATE_DURATION}ms,
    background-color ${DEFAULT_ANIMATE_DURATION}ms;
  :hover {
    background-color: #ffffff11;
    transform: translateY(-3px);
  }

  ${props => props.theme.breakpoints.up('res1024')} {
    column-gap: 40px;
    padding: 23px 41px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res1024')} {
    column-gap: 20px;
    padding: 10px 5%;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    padding: 10px 3.5%;
    column-gap: 5px;
  }
`

const ApostleImageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ApostleImageCircle = styled(Box, noForwardProps)<CircleProps>`
  aspect-ratio: 1;
  align-items: center;
  background: linear-gradient(
    180deg,
    ${props => props.__color} 0%,
    ${props => props.__color} 50%,
    ${props => darken(0.2, props.__color)} 75%,
    ${props => darken(0.4, props.__color)} 100%
  );
  border-radius: 50%;
  display: flex;
  justify-content: center;
  width: 70px;
`

const ApostleImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 90%;
`

const ApostleClassImage = styled(NextImage)`
  aspect-ratio: 1;
  filter: brightness(1.2);
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
`

const ApostleClassSquares = styled(Box)`
  align-items: center;
  display: flex;
  margin-top: -22px;
  height: 40px;
  position: relative;
  gap: 16px;
`

const ApostleClassCenter = styled(Box)`
  aspect-ratio: 1;
  background-color: #555;
  border: solid 2px #00fcfd;
  box-shadow: 0px 0px 5px 5px #00fcfd33;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 22px;
`

const ApostleClassSquare = styled(Box)`
  aspect-ratio: 1;
  background-color: #000000aa;
  border: solid 2px #ffffff88;
  transform: rotate(45deg);
  width: 12px;
`

const ApostleName = styled(Typography)`
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  margin-top: 8px;
`

const Skills = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  width: 100%;
`

const Skill = styled(ApostleSkillItem)`
  ${props => props.theme.breakpoints.down('res1024')} {
    .Custom-Image-Container {
      .Custom-Image {
        width: 80px;
      }
    }
  }
`

const Share = styled(ParagraphBlockLinkButton)`
  min-width: 20px;
`

const Content = (props: Props) => {
  const { apostles, previewSrc, layoutProps } = props

  const { getSkill } = useApostleSkillsContext()

  return (
    <Container>
      <PreviewContainer>
        <Preview
          src={previewSrc}
          __aspectRatio={layoutProps.previewAspectRatio}
        />
      </PreviewContainer>
      <Apostles>
        {apostles.map((apostle, index) => {
          const key = index

          return (
            <ApostleContainer key={key}>
              <ApostleImageContainer>
                <ApostleImageCircle __color={apostle.class.color}>
                  <ApostleImage src={apostle.class.image} />
                </ApostleImageCircle>
                <ApostleClassSquares>
                  <ApostleClassSquare />
                  <ApostleClassSquare />
                  <ApostleClassCenter />
                  <ApostleClassImage src={apostle.class.classImage} />
                </ApostleClassSquares>
                <ApostleName>{apostle.class.title}</ApostleName>
              </ApostleImageContainer>
              <Skills>
                {R.map(skillId => {
                  const skill = getSkill(skillId, 0)

                  const key = `${apostle.class.title}-${skillId}`

                  return <Skill hoverMode key={key} skill={skill} />
                }, apostle.skills)}
              </Skills>
              <Share href={apostle.href} />
            </ApostleContainer>
          )
        })}
      </Apostles>
    </Container>
  )
}

const PvpBuildExampleBlockView = (props: Props) => {
  return (
    <ApostleSkillsProvider>
      <Content {...props} />
    </ApostleSkillsProvider>
  )
}

type CircleProps = {
  __color: string
}

type Props = {
  previewSrc: string
  apostles: PvpBuildExampleBlock['value']['apostles']
  layoutProps: PvpBuildExampleBlock['layoutProps']
}

export default PvpBuildExampleBlockView
